import { formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, cn } from "@ncp/ui";
import { OPEN_STATUSES, statusTone, type RequestRow } from "./api";

/** A dense list of requests: reference, addressee, subject, the date the answer is due and where it stands. Clicking opens the review sheet. */
export function RequestTable({ rows, onOpen, showAbout }: { rows: RequestRow[]; onOpen: (id: string) => void; showAbout?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  return (
    <div className="overflow-x-auto rounded-lg border border-sand-200" tabIndex={0}>
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-sand-50 text-xs text-sand-700">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t("backoffice:requests.columns.reference")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("backoffice:requests.columns.addressee")}</th>
            {showAbout ? <th className="px-3 py-2 text-start font-medium">{t("backoffice:requests.columns.about")}</th> : null}
            <th className="px-3 py-2 text-start font-medium">{t("backoffice:requests.columns.due")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("backoffice:requests.columns.status")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-sand-100">
          {rows.map((r) => {
            const open = OPEN_STATUSES.includes(r.status);
            return (
              <tr key={r.id} className="cursor-pointer hover:bg-sand-50" onClick={() => onOpen(r.id)} data-request={r.reference}>
                <td className="px-3 py-2 align-top">
                  <span className="whitespace-nowrap font-mono text-xs text-aegold-800">{r.reference}</span>
                  <p className="text-[11px] text-muted-foreground">{t(`backoffice:requests.kind.${r.kind}`)}</p>
                </td>
                <td className="px-3 py-2 align-top">
                  <p className="font-medium" dir="auto">{ar ? r.recipientNameAr : r.recipientName}</p>
                  {r.recipientSector ? <p className="text-[11px] text-muted-foreground" dir="auto">{ar ? r.recipientSectorAr ?? r.recipientSector : r.recipientSector}</p> : null}
                </td>
                {showAbout ? <td className="max-w-72 px-3 py-2 align-top"><p className="line-clamp-2 text-xs" dir="auto">{r.caseNumber ? <span className="me-1 font-mono">{r.caseNumber}</span> : null}{ar && r.subjectAr ? r.subjectAr : r.subject}</p></td> : null}
                <td className="whitespace-nowrap px-3 py-2 align-top text-xs">
                  <span className={cn(open && r.overdue ? "text-danger-700" : "text-aeblack-900")}>{formatDate(r.dueOn, locale)}</span>
                  {open ? <p className={cn("text-[11px]", r.overdue ? "text-danger-700" : "text-muted-foreground")}>{r.overdue ? t("backoffice:requests.overdue") : t("time.daysLeft", { count: r.daysLeft })}</p> : null}
                </td>
                <td className="px-3 py-2 align-top">
                  <Badge size="sm" dot variant={statusTone(r.status, r.overdue)}>
                    {r.status === "Completed" ? t(`backoffice:requests.completed.${r.kind}`) : t(`backoffice:requests.status.${r.status}`)}
                  </Badge>
                  {r.extensionRequestedTo && open ? <p className="mt-0.5 text-[11px] text-warning-700">{t("backoffice:requests.events.ExtensionRequested")} · {formatDate(r.extensionRequestedTo, locale)}</p> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
